// frontend/src/components/LogViewer.js
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadLogs, deleteLog, deleteAllLogs } from '../redux/slices/tradingSlice';
import { FaTrash, FaTrashAlt, FaDownload, FaFilter } from 'react-icons/fa';
import toast from 'react-hot-toast';

const LogViewer = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { logs } = useSelector((state) => state.trading);
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      dispatch(loadLogs(user.uid, { level: levelFilter, source: sourceFilter }));
    }
  }, [user, levelFilter, sourceFilter]);

  // Helper function to safely format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    
    // Handle Firestore Timestamp
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleString();
    }
    
    // Handle regular Date object
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    
    // Handle timestamp in seconds
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toLocaleString();
    }
    
    // Handle ISO string
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleString();
    }
    
    return '-';
  };

  const getLevelIcon = (level) => {
    switch(level) {
      case 'ERROR': return '🔴';
      case 'WARNING': return '🟡';
      case 'INFO': return '🟢';
      default: return '🔵';
    }
  };

  const getLevelClass = (level) => {
    switch(level) {
      case 'ERROR': return 'level-error';
      case 'WARNING': return 'level-warning';
      case 'INFO': return 'level-info';
      default: return 'level-debug';
    }
  };

  const handleDeleteLog = async (logId) => {
    if (window.confirm('Delete this log entry?')) {
      await dispatch(deleteLog(user.uid, logId));
    }
  };

  const handleDeleteAllLogs = async () => {
    if (window.confirm('Delete ALL logs? This action cannot be undone.')) {
      await dispatch(deleteAllLogs(user.uid));
    }
  };

  const handleDeleteOldLogs = async () => {
    if (window.confirm('Delete logs older than 30 days?')) {
      await dispatch(deleteAllLogs(user.uid, 30));
    }
  };

  const handleExportLogs = () => {
    const exportData = logs.map(log => ({
      id: log.id,
      timestamp: formatTimestamp(log.timestamp),
      level: log.level,
      source: log.source,
      message: log.message
    }));
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `tradehawk_logs_${new Date().toISOString()}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    toast.success('Logs exported');
  };

  const levelOptions = ['all', 'INFO', 'WARNING', 'ERROR', 'DEBUG'];
  const sourceOptions = ['all', 'system', 'trading-bot', 'market-detector'];

  if (!logs || logs.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2>System Logs</h2>
          <div className="log-actions">
            <button onClick={() => setShowFilters(!showFilters)} className="btn btn-secondary btn-small">
              <FaFilter /> Filters
            </button>
            <button onClick={handleExportLogs} className="btn btn-secondary btn-small">
              <FaDownload /> Export
            </button>
            <button onClick={handleDeleteOldLogs} className="btn btn-warning btn-small">
              Delete >30 days
            </button>
            <button onClick={handleDeleteAllLogs} className="btn btn-danger btn-small">
              <FaTrashAlt /> Delete All
            </button>
          </div>
        </div>
        <p className="text-muted">No logs available</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>System Logs</h2>
        <div className="log-actions">
          <button onClick={() => setShowFilters(!showFilters)} className="btn btn-secondary btn-small">
            <FaFilter /> Filters
          </button>
          <button onClick={handleExportLogs} className="btn btn-secondary btn-small">
            <FaDownload /> Export
          </button>
          <button onClick={handleDeleteOldLogs} className="btn btn-warning btn-small">
            Delete >30 days
          </button>
          <button onClick={handleDeleteAllLogs} className="btn btn-danger btn-small">
            <FaTrashAlt /> Delete All
          </button>
        </div>
      </div>
      
      {showFilters && (
        <div className="log-filters">
          <div className="filter-group">
            <label>Level:</label>
            <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
              {levelOptions.map(opt => (
                <option key={opt} value={opt}>{opt.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Source:</label>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              {sourceOptions.map(opt => (
                <option key={opt} value={opt}>{opt.toUpperCase()}</option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      <div className="logs-table">
        <div className="table-responsive">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Level</th>
                <th>Source</th>
                <th>Message</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className={`log-row ${getLevelClass(log.level)}`}>
                  <td className="log-time">{formatTimestamp(log.timestamp)}</td>
                  <td>
                    <span className={`log-level ${getLevelClass(log.level)}`}>
                      {getLevelIcon(log.level)} {log.level}
                    </span>
                  </td>
                  <td className="log-source">{log.source || 'system'}</td>
                  <td className="log-message">{log.message}</td>
                  <td>
                    <button 
                      onClick={() => handleDeleteLog(log.id)} 
                      className="delete-log-btn"
                      title="Delete log"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="log-stats">
        <small>
          Showing {logs.length} logs | 
          Auto-delete logs older than 30 days
        </small>
      </div>
    </div>
  );
};

export default LogViewer;